import React, { useEffect, useState } from "react";
import { Activity, Ban, Download, Eye, Link2, Mail } from "lucide-react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import type { ProposalActivity, ProposalLinkRow } from "@/types/proposal";
import { useConfirm } from "@/context/ConfirmContext";
import { dateLocale, withClock } from "@/lib/dateTime";

const fmtDateTime = (d?: string | null) =>
  d ? new Date(d).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })) : "";

/** 75 → "1 min 15 s" */
export const readingTime = (s: number) => {
  if (!s) return "under a minute";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h} h ${m} min`;
  if (m) return `${m} min${s % 60 >= 30 ? " 30 s" : ""}`;
  return `${s} s`;
};

const CHANNEL_ICON: Record<string, React.ElementType> = { EMAIL: Mail, WHATSAPP: WhatsAppIcon, LINK: Link2 };

/** Who the proposal was sent to and what they did with it */
export function ProposalActivityCard({ proposalId, canRevoke, refreshKey, canCopyLink = false, onLinkCreated }: { proposalId: string; canRevoke: boolean; refreshKey: number; canCopyLink?: boolean; onLinkCreated?: () => void }) {
  const [copying, setCopying] = useState(false);
  const confirm = useConfirm();
  const { success, error } = useToast();
  const [data, setData] = useState<ProposalActivity | null>(null);

  const load = () =>
    api
      .get(`/api/Proposal/Activity/${proposalId}`)
      .then((res) => setData(unwrapObject<ProposalActivity>(res)))
      .catch(() => setData(null));

  useEffect(() => {
    load();
  }, [proposalId, refreshKey]);

  // A link's secret is stored only as a hash, so an old link cannot be shown again: this makes a fresh one and copies it
  const copyNewLink = async () => {
    setCopying(true);
    try {
      const res = unwrapObject<{ url: string }>(await api.post(`/api/Proposal/Send/${proposalId}`, { channel: "LINK" }));
      await navigator.clipboard.writeText(res.url);
      success("Link copied", "A new link for this proposal is on your clipboard.");
      load();
      onLinkCreated?.();
    } catch (err: any) {
      error("Couldn't make a link", err.message || "Something went wrong");
    } finally {
      setCopying(false);
    }
  };

  const revoke = async (l: ProposalLinkRow) => {
    const ok = await confirm({
      title: `Switch off the link ${l.recipient_name ? `sent to ${l.recipient_name}` : "you created"}?`,
      message: "Anyone opening it will see that it's no longer valid.",
      confirmLabel: "Switch off",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.post(`/api/Proposal/RevokeLink/${proposalId}/${l.id}`);
      success("Link switched off", "It no longer opens the proposal");
      load();
    } catch (err: any) {
      error("Couldn't switch it off", err.message || "Something went wrong");
    }
  };

  if (!data || data.links.length === 0) return null;
  const linkName = new Map(data.links.map((l) => [l.id, l.recipient_name || "Link"]));

  return (
    <div className="bg-card border rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b">
        <h3 className="text-sm font-bold flex items-center gap-2">
          <Activity className="size-4 text-primary" /> Customer activity
        </h3>
        <p className="text-[11px] text-muted-foreground mt-1">
          {data.views.some((v) => v.event === "VIEW")
            ? `Read for ${readingTime(data.total_seconds)} in total${data.downloads ? ` · downloaded ${data.downloads}×` : ""}`
            : "Not opened yet"}
        </p>
      </div>

      {data.reading && data.reading.length > 0 && (
        <div className="flex flex-col gap-1.5 border-b px-4 py-3">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">What they read</h4>
          {(() => {
            const top = Math.max(...data.reading!.map((r) => r.seconds));
            return data.reading!.map((r) => (
              <div key={r.id} className="grid grid-cols-[96px_minmax(0,1fr)_48px] items-center gap-2 text-xs">
                <span className="truncate">{r.title}</span>
                <span className="h-1.5 rounded-full bg-muted">
                  <span className={`block h-full rounded-full ${r.seconds === top ? "bg-gold" : "bg-primary"}`} style={{ width: `${Math.max(6, Math.round((r.seconds / top) * 100))}%` }} />
                </span>
                <span className="text-right font-mono">{readingTime(r.seconds)}</span>
              </div>
            ));
          })()}
        </div>
      )}

      {canCopyLink && (
        <div className="flex items-center justify-between gap-2 border-b px-4 py-2">
          <span className="text-[11.5px] text-muted-foreground">Share it your own way</span>
          <button type="button" onClick={copyNewLink} disabled={copying} className="text-xs font-medium text-primary hover:underline disabled:opacity-50">
            Copy a new link
          </button>
        </div>
      )}

      <ul className="divide-y">
        {data.links.map((l) => {
          const Icon = CHANNEL_ICON[l.channel] || Link2;
          return (
            <li key={l.id} className="px-4 py-2.5 text-xs">
              <div className="flex items-start gap-2">
                <Icon className="size-3.5 mt-0.5 text-muted-foreground" color="currentColor" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">
                    {l.recipient_name || "Unnamed link"}
                    {l.revoked_at && <span className="ml-1.5 text-[10px] font-semibold uppercase text-destructive">Switched off</span>}
                  </div>
                  <div className="text-muted-foreground truncate">{l.recipient || (l.channel === "LINK" ? "Link copied by the rep" : "")}</div>
                  <div className="text-muted-foreground">
                    Sent {fmtDateTime(l.sent_at)}
                    {l.sent_by && ` by ${l.sent_by}`}
                  </div>
                  <div className={l.view_count ? "text-success" : "text-muted-foreground"}>
                    {l.view_count
                      ? `Opened ${l.view_count}× · last ${fmtDateTime(l.last_viewed_at)} · ${readingTime(l.seconds)}`
                      : "Not opened"}
                  </div>
                </div>
                {canRevoke && !l.revoked_at && (
                  <button
                    type="button"
                    title="Switch off this link"
                    onClick={() => revoke(l)}
                    className="size-7 inline-flex items-center justify-center rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive shrink-0"
                  >
                    <Ban className="size-3.5" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {data.views.length > 0 && (
        <div className="border-t">
          <div className="px-4 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Recent opens</div>
          <ul className="pb-2 max-h-56 overflow-y-auto">
            {data.views.slice(0, 20).map((v) => (
              <li key={v.id} className="px-4 py-1 text-[11px] flex items-center gap-2">
                {v.event === "DOWNLOAD" ? <Download className="size-3 text-muted-foreground" /> : <Eye className="size-3 text-muted-foreground" />}
                <span className="flex-1 min-w-0 truncate">
                  {linkName.get(v.link_id)} · {v.device}
                </span>
                <span className="text-muted-foreground whitespace-nowrap">
                  {fmtDateTime(v.at)}
                  {v.event === "VIEW" && ` · ${readingTime(v.seconds)}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
